import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { getThgPrices, updateThgPrices } from "../../services/clearing/thg-prices";
import type { ThgPricesFailure, ThgPricesResult, ThgYearPrices } from "../../types/thgPrices";
import { COCHARGE_REVENUE_TEXTS } from "../StationPerformance/StationPerformance.consts";
import { formatBerlinDate } from "../ThgAnnualSummary/utils/thgFormat";

interface ThgPricesDialogProps {
  visible: boolean;
  onHide: () => void;
  /** Called after a price was saved, so the page can reload the figures that use it. */
  onSaved: () => void;
}

interface Draft {
  incoming: number | null;
  outgoing: number | null;
}

const draftOf = (row: ThgYearPrices): Draft => ({
  incoming: row.incomingEurPerMwh,
  outgoing: row.outgoingEurPerMwh,
});

const isLocked = (row: ThgYearPrices) => row.outgoingLocked === true;

const isPositive = (v: number | null): v is number => v !== null && v !== undefined && v > 0;

const sourceText = (row: ThgYearPrices): string => {
  if (row.source === "DEFAULT") return "default (from config)";
  if (row.source === "STORED") {
    return `last changed by ${row.updatedBy ?? "unknown"} on ${formatBerlinDate(row.updatedAt)}`;
  }
  return "not configured";
};

const priceFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

const creditNoteWarning = (year: number, old: number | null) =>
  COCHARGE_REVENUE_TEXTS.creditNoteWarning(year, old === null ? "—" : priceFormat.format(old));

/**
 * CLR-296 the THG prices per year: what cocharge receives (incoming) and what it pays station owners
 * (outgoing). One row and one Save per year, so a change to one year cannot touch another.
 */
const ThgPricesDialog: React.FC<ThgPricesDialogProps> = ({ visible, onHide, onSaved }) => {
  const toast = useRef<Toast>(null);
  const [rows, setRows] = useState<ThgYearPrices[]>([]);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  // True from the start: the dialog fetches as soon as it is visible, so the first paint is the spinner.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<number | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [confirmYear, setConfirmYear] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = (await getThgPrices()) as ThgPricesResult<ThgYearPrices[]>;
    if (result.success) {
      setRows(result.data);
      setDrafts(Object.fromEntries(result.data.map((r) => [r.year, draftOf(r)])));
    } else {
      setLoadError((result as ThgPricesFailure).message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (visible) load();
  }, [visible, load]);

  const setDraft = (year: number, patch: Partial<Draft>) =>
    setDrafts((current) => ({ ...current, [year]: { ...current[year], ...patch } }));

  const isChanged = (row: ThgYearPrices) => {
    const d = drafts[row.year];
    return !!d && (d.incoming !== row.incomingEurPerMwh || d.outgoing !== row.outgoingEurPerMwh);
  };

  const canSave = (row: ThgYearPrices) => {
    const d = drafts[row.year];
    return !!d && isPositive(d.incoming) && isPositive(d.outgoing) && isChanged(row) && saving === null;
  };

  const save = async (row: ThgYearPrices) => {
    const d = drafts[row.year];
    if (!d || !isPositive(d.incoming) || !isPositive(d.outgoing)) return;
    setSaving(row.year);
    setRowErrors((current) => {
      const { [row.year]: _dropped, ...rest } = current;
      return rest;
    });
    const result = (await updateThgPrices(row.year, {
      incomingEurPerMwh: d.incoming,
      outgoingEurPerMwh: d.outgoing,
    })) as ThgPricesResult<ThgYearPrices>;
    setSaving(null);
    if (result.success) {
      setRows((current) => current.map((r) => (r.year === row.year ? result.data : r)));
      setDraft(row.year, draftOf(result.data));
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: `THG prices for ${row.year} saved`,
        life: 3000,
      });
      onSaved();
    } else {
      setRowErrors((current) => ({ ...current, [row.year]: (result as ThgPricesFailure).message }));
    }
  };

  const requestSave = (row: ThgYearPrices) => {
    const outgoingChanged = drafts[row.year]?.outgoing !== row.outgoingEurPerMwh;
    if (!isLocked(row) && row.creditNotesIssued && outgoingChanged) {
      setConfirmYear(row.year);
      return;
    }
    save(row);
  };

  const confirmRow = rows.find((r) => r.year === confirmYear);

  const renderRow = (row: ThgYearPrices) => {
    const d = drafts[row.year] ?? draftOf(row);
    const error = rowErrors[row.year];
    return (
      <div key={row.year} className="surface-border border-bottom-1 py-3" data-testid={`thg-prices-row-${row.year}`}>
        <div className="flex flex-wrap align-items-end gap-3">
          <span className="font-semibold text-xl w-4rem">{row.year}</span>
          <div className="flex flex-column gap-1">
            <label htmlFor={`thg-in-${row.year}`} className="text-sm text-color-secondary">
              Incoming €/MWh
            </label>
            <InputNumber
              inputId={`thg-in-${row.year}`}
              value={d.incoming}
              onValueChange={(e) => setDraft(row.year, { incoming: e.value ?? null })}
              min={0.01}
              minFractionDigits={0}
              maxFractionDigits={2}
              locale="de-DE"
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor={`thg-out-${row.year}`} className="text-sm text-color-secondary">
              Outgoing €/MWh
            </label>
            <InputNumber
              inputId={`thg-out-${row.year}`}
              disabled={isLocked(row)}
              value={d.outgoing}
              onValueChange={(e) => setDraft(row.year, { outgoing: e.value ?? null })}
              min={0.01}
              minFractionDigits={0}
              maxFractionDigits={2}
              locale="de-DE"
            />
          </div>
          <Button
            label="Save"
            icon="pi pi-check"
            disabled={!canSave(row)}
            loading={saving === row.year}
            onClick={() => requestSave(row)}
          />
        </div>
        <div className="text-sm text-color-secondary mt-2">{sourceText(row)}</div>
        {isLocked(row) && (
          <div className="text-sm text-color-secondary mt-2">{COCHARGE_REVENUE_TEXTS.outgoingLocked(row.year)}</div>
        )}
        {error && (
          <Message severity="error" className="w-full justify-content-start mt-2" text={error} />
        )}
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        visible={confirmYear !== null}
        onHide={() => setConfirmYear(null)}
        message={confirmRow ? creditNoteWarning(confirmRow.year, confirmRow.outgoingEurPerMwh) : ""}
        header="Credit notes already issued"
        icon="pi pi-exclamation-triangle"
        acceptLabel="Save anyway"
        rejectLabel="Cancel"
        accept={() => {
          if (confirmRow) save(confirmRow);
        }}
      />
      <Dialog
        header="THG prices"
        visible={visible}
        onHide={onHide}
        style={{ width: "min(46rem, 95vw)" }}
        modal
      >
        <p className="text-sm text-color-secondary mt-0">
          Incoming is what cocharge receives per MWh; outgoing is what we pay station owners.
        </p>
        {loading && (
          <div className="flex justify-content-center p-4" role="status">
            <ProgressSpinner style={{ width: "2.5rem", height: "2.5rem" }} strokeWidth="4" />
          </div>
        )}
        {!loading && loadError && (
          <div className="flex flex-column gap-3 align-items-start">
            <Message severity="error" className="w-full justify-content-start" text={loadError} />
            <Button label="Retry" icon="pi pi-refresh" onClick={load} />
          </div>
        )}
        {!loading && !loadError && rows.map(renderRow)}
      </Dialog>
    </>
  );
};

export default ThgPricesDialog;
